import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  Grid,
  Icon
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { getQuoteProProductsApi, updateCustomerPushCartApi } from 'api';
import { setPatientsGroupCart } from 'redux/actions/quote';
import { TextFormattedNumber } from 'components';
import SelectedPackageItem from './SelectedPackageItem';
import { useStyles } from './styles';
import { calculateTotals } from './utils/calculateTotals';


const SelectedPackageDetails = ({
  selectedPackage,
  setSelectedPackage,
  handleCancel
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{id?: string}>();
  const isPatientGroupActive = location.pathname.includes('/quoting/group/');

  const {
    name,
    selectedProducts,
    products
  } = selectedPackage;

  const {
    customerPushCart,
    quoteProducts,
    currentMarketingSegment,
    currentQuoteCustomer,
    quoteProductsByGroup
  } = useSelector((state: IReduxState) => state.quote);

  useEffect(() => {
    if (!Object.keys(quoteProductsByGroup).length || !quoteProducts.length) {
      getQuoteProProductsApi({ dispatch });
    }
  }, []);

  const selectedItemsInitialValues = selectedProducts.map((p) => {
    const {
      sku,
      qty,
      price: updatedPrice,
      custom_subscription_price,
      product_option: {
        extension_attributes: {
          subscription_id,
          quotepro_discounts
        },
      },
    } = p;
    const name = products[sku]?.product?.name;

    const discountType = quotepro_discounts?.price_type || '';
    const discount = quotepro_discounts?.price || '';
    const isSubscriptionActive = products[sku]?.product?.subscription_active == 1;
    const price = products[sku]?.product?.price;
    const fixedPrice = updatedPrice ? updatedPrice : price || 0;
    const selectedItemGroup = products[sku]?.product?.catalog_quotepro_groups?.toString().split(',')[0] || '';

    return {
      selectedItem: name || '',
      sku,
      qty,
      subscriptionDiscount: custom_subscription_price ? 'allCycles' : 'firstBill',
      custom_subscription_price: custom_subscription_price || null,
      discount,
      discountType,
      fixedPrice,
      subscription_id: isSubscriptionActive ? subscription_id || '' : '',
      selectedItemGroup
    };
  });

  const handleAddItemsToPushCart = async (values) => {
    const selectedItems = values.items.map((item) => {
      const { sku, discountType, discount, qty, subscription_id, fixedPrice, custom_subscription_price } = item;

      const selectedCartItem = {
        sku,
        qty,
        price: fixedPrice,
        custom_subscription_price,
        product_option: {
          extension_attributes: {
            quotepro_discounts: {
              price_type: discountType,
              price: discount,
            },
            subscription_id
          }
        }
      };

      return selectedCartItem;
    });

    if (isPatientGroupActive) {
      const updatedPatientsGroupCart = dispatch(setPatientsGroupCart(selectedItems));

      if (updatedPatientsGroupCart && currentMarketingSegment?.value) {
        history.push(`/quoting/group/${currentMarketingSegment.value}/quote-workspace/summary`);
      }
    } else {
      const pushCart = {
        ...customerPushCart,
        customer: currentQuoteCustomer,
        selectedItems,
        name
      };

      const updatedPushCart = await updateCustomerPushCartApi({
        dispatch,
        pushCartId: customerPushCart._id,
        pushCart
      });

      if (updatedPushCart) {
        history.push(`/quoting/profile/${id}/quote-workspace/summary`);
      }
    }
  };

  return (
    <Formik
      initialValues={{
        items: selectedItemsInitialValues
      }}
      onSubmit={handleAddItemsToPushCart}
      validationSchema={yup.object({items: yup.array().of(
        yup.object().shape({
          sku: yup
            .string()
            .test('sku', 'Product is disabled or removed. Please remove it to continue.', function () {
              const { sku } = this.parent;
              const isProductEnabled = quoteProducts.find((p) => p.sku === sku)?.status === 1;

              return isProductEnabled;
            }),
          fixedPrice: yup
            .number()
            .typeError('Allowed only numerical symbols')
            .positive('Value must be positive')
            .min(0, 'Price should be positive number')
            .test(
              'price',
              'The unit price can\'t be lower discount', function (val) {
                const { discountType, discount, sku, savings, qty } = this.parent;

                if (sku?.includes('bdsku')) {
                  return true;
                }

                if (discountType === 'fixed' && discount > val) {
                  return false;
                }

                if (!discount && Math.abs(savings / qty) > val) {
                  return false;
                }

                return true;
              }
            )
            .required('Field is required'),
          discountType: yup
            .string(),
          discount: yup
            .string()
            .when(
              'discountType',
              {
                is: (val) => !!val,
                then: yup
                  .string()
                  .required('Field is required')
              }
            ),
          qty: yup
            .number()
            .typeError('Field is required')
            .integer('Only integer numbers allowed')
            .min(1, 'Only positive values allowed')
            .test('max', 'There is not enough items in stock', function (value) {
              const { sku } = this.parent;
              const productItemsInCustomerPushCart = customerPushCart?.selectedItems.reduce((qty, item) => {
                if (item.sku === sku) {
                  qty += item.qty;
                }

                return qty;
              }, 0);

              const product = quoteProducts.find((p) => p.sku === sku);

              if (
                product?.type_id === 'simple' &&
                  (value > ((product?.extension_attributes?.stock_item?.qty || 0) - productItemsInCustomerPushCart))
              ) {
                return false;
              }

              return true;
            })
            .required('Field is required'),
          subscription_id: yup
            .string()
            .test(
              'subscription_id',
              'Field is required', function (val) {
                const { sku } = this.parent;
                const isSubscriptionActive = products[sku]?.product?.subscription_active === '1';
                const subscriptionOptions = products[sku]?.product?.subscription_options;

                if (subscriptionOptions?.length === 0) {
                  return true;
                }

                if (subscriptionOptions?.length > 0 && isSubscriptionActive) {
                  return !!val?.length;
                }

                return true;
              }
            )
        })
      )})}
    >
      {({
        values,
        errors,
        touched,
        isValid,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        setValues,
        handleSubmit,
        validateForm,
        setErrors
      }) => {
        const {
          grandTotal,
          discountTotal,
          totalAdditionalSubscriptionPrice
        } = calculateTotals(values?.items, products);
        return (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center" className={classes.quoteGroupDotted}>
              <Grid item xs={12}>
                <div className="flex flex-row items-center mb-5">
                  <Button
                    size="small"
                    onClick={() => setSelectedPackage(null)}
                    className="mr-2 border-1-light-grey"
                  >
                    <Icon fontSize="small">arrow_back</Icon>
                  </Button>
                  <div className="text-18 font-bold">{name}</div>
                </div>
                {values?.items?.map((selectedItem, index) => (
                  <SelectedPackageItem
                    key={`selectedItem-${index}`}
                    index={index}
                    selectedItem={selectedItem}
                    productData={products[selectedItem.sku]}
                    setSelectedPackage={setSelectedPackage}
                    formikProps={{
                      values,
                      errors,
                      touched,
                      isValid,
                      setFieldValue,
                      setFieldTouched,
                      handleChange,
                      handleBlur,
                      setValues,
                      handleSubmit,
                      validateForm,
                      setErrors
                    }}
                  />
                ))}
              </Grid>
              <Grid container justifyContent="flex-end" className="px-4">
                <Grid item xs={12} lg={2}>
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">Subtotal</div>
                      <div className="text-right">
                        <TextFormattedNumber
                          value={grandTotal + discountTotal}
                          prefix="$"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">Discount</div>
                      <div className="text-right">
                        <TextFormattedNumber
                          value={discountTotal}
                          prefix="$"
                        />
                      </div>
                    </div>
                    {totalAdditionalSubscriptionPrice !== 0 ?
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">Per month, paid annually:</div>
                        <div className="text-right">
                          <TextFormattedNumber
                            value={totalAdditionalSubscriptionPrice}
                            prefix="$"
                          />
                        </div>
                      </div>
                      :
                      null
                    }
                  </>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold">TOTAL</div>
                    <div className="text-right">
                      <TextFormattedNumber
                        value={grandTotal}
                        prefix="$"
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <div className="flex flex-row align-center justify-between mb-4">
              <Button
                variant="outlined"
                color="primary"
                className="uppercase"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isValid}
              >
                Save
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default SelectedPackageDetails;
